import { Component, OnInit, Input, Output, ViewChild, HostListener, EventEmitter, OnDestroy, AfterViewInit, ElementRef, Renderer2, Inject } from '@angular/core';
import { SearchObjectService } from '../../services/search-object.service';
import { MapViewService } from './map-view.service';
import { debounceTime } from 'rxjs/operators'
import { Subscription } from 'rxjs';
import * as atlas from 'azure-maps-control';
import { AuthenticationType } from 'azure-maps-control';
import { MapService } from '../../services/map.service';
import { AZURE_MAP_CLIENT_ID } from 'src/app/tokens';

@Component({
  selector: 'app-map-view',
  templateUrl: './map-view.component.html',
  styleUrls: ['./map-view.component.scss']
})
export class MapViewComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() SearchData: any;
  @Input() upmcData: any;
  @Input() ShowSearchDataDefault: any;
  @Input() ProviderDetailLocations: any[];
  @Input() showPromoted: boolean;
  @Input() showSheets: boolean;

  map: any;
  defaultLat: number = 40.4436;
  defaultLng: number = -79.9548;
  options = {
    center: {
      lat: 40.4436,
      lng: -79.9548
    },
    zoomControl: false,
    streetViewControl: false,
    fullscreenControl: false,
    mapTypeControl: false,
    zoom: 11,
    clickableIcons: false,
    disableDoubleClickZoom: true,
  };

  bounds = [];
  markerPopup = null;
  page = 1;
  detailsCount = 0;
  gmarkers = [];
  letterStore = [];
  currentLocationPushed = false;
  setMap = false;
  updateMapSubscription: Subscription;
  updateDetailsMapSubscription: Subscription;
  mapSubscription: Subscription;

  MAP_MARKER_ICON_URL = 'https://cdn.upmchp.com/providerdirectory/assets/icons/map-marker.svg';
  MAP_MARKER_UPMC_ICON_URL = 'https://cdn.upmchp.com/providerdirectory/assets/icons/map-marker-upmc.svg';
  MAP_MARKER_HIGHLIGHT_ICON_URL = 'https://cdn.upmchp.com/providerdirectory/assets/icons/map-marker-highlight.svg';
  MAP_MARKER_CURRENT_LOCATION = 'https://cdn.upmchp.com/providerdirectory/assets/icons/current-location.png';


  @Output() markerClickEvent = new EventEmitter<string>();
  @ViewChild('Map', { static: false }) mapContainer: ElementRef;

  @HostListener('window:resize')
  onWindowResize() {
    this.fitBounds();
  }

  constructor(private searchObjectService: SearchObjectService,
    private mapViewService: MapViewService,
    private renderer: Renderer2,
    private mapService: MapService,
    @Inject(AZURE_MAP_CLIENT_ID) private azureMapClientID: string
  ) {
    this.updateMapSubscription = this.mapViewService.updateMapMarker.pipe(debounceTime(100)).subscribe(() => {
      this.updateMarkers();
    });

    this.updateDetailsMapSubscription = this.mapViewService.updateDetailsMapMarker.subscribe(() => {
      this.updateMarkers();
    });
  }

  ngOnInit() {
  }

  ngAfterViewInit() {
    this.mapService.getApiKeyObservable().subscribe((azurekey: string) => {
      this.map = new atlas.Map(this.mapContainer.nativeElement, {
        center: [this.defaultLng, this.defaultLat],
        zoom: 11,
        language: 'en-US',
        showLogo: false,
        showFeedbackLink: false,
        dragRotateInteraction: false,
        authOptions: {
          authType: AuthenticationType.anonymous,
          clientId: this.azureMapClientID,
          getToken: (resolve: any) => {
            resolve(azurekey);
          }
          // subscriptionKey: azurekey
        }
      });
      //Wait until the map resources are ready.
      this.map.events.add('ready', () => {
        this.resetMap(this.page);
        this.resetDetailsMap(this.detailsCount);
        if (this.showSheets) {
          this.generateSheetMarkers(this.SearchData);
        }
        this.fitBounds();
      });
    });
  }

  ngOnDestroy() {
    if (this.updateMapSubscription) {
      this.updateMapSubscription.unsubscribe();
    }

    if (this.updateDetailsMapSubscription) {
      this.updateDetailsMapSubscription.unsubscribe();
    }
  }


  @Input()
  set level(bool: boolean) {
    if (this.setMap) {
      this.resetMap();
    }
  }

  @Input()
  set SetMap(bool: boolean) {
    if (bool) {
      this.setMap = true;
    } else {
      this.setMap = false;
    }
  }

  @Input()
  set active(bool: boolean) {
    if (bool) {
      this.updateMarkers();
    }
  }

  @Input()
  set CurrentPageNum(page: number) {
    this.page = page;
    if (this.setMap) {
      this.resetMap(page);
    }
  }

  @Input()
  set ShowDetailData(count: number) {
    this.detailsCount = count;
    this.resetDetailsMap(count);
  }

  @Input()
  set showSheetsToggle(bool: boolean) {
    if (bool) {
      this.generateSheetMarkers(this.SearchData);
    }
  }

  resetMap(page?) {
    if (this.SearchData) {
      this.resetMarkers();
      this.currentLocationPushed = false;
      this.generateMarkers(this.SearchData, page ? page : 1, this.ShowSearchDataDefault, this.upmcData);
      if (!this.currentLocationPushed) {
        this.pushCurrentLocation();
      }
      if (this.map) {
        this.gmarkers.forEach(m => {
          this.map.markers.add(m);
        });
        this.updateMarkers();
      }
      this.fitBounds();
    }
  }

  resetDetailsMap(count: number) {
    if (this.ProviderDetailLocations) {
      this.resetMarkers();
      this.currentLocationPushed = false;

      this.generateDetailMarkers(this.ProviderDetailLocations, count);
      if (!this.currentLocationPushed) {
        this.pushCurrentLocation();
      }
      if (this.map) {
        this.gmarkers.forEach(m => {
          this.map.markers.add(m);
        });
      }
      this.fitBounds();
    }
  }

  generateMarkers(MapData, page, count, upmcData) {
    if (MapData) {
      for (let i = (page * count) - count; i < ((page * count) < MapData.length ? (page * count) : MapData.length); i++) {
        const location = MapData[i].locations[0];
        if (location) {
          this._markerInfo(MapData[i], i);
        }
      }

      // promoted section markers
      if (upmcData && page === 1) {
        upmcData.forEach(MapData => {
          const exist = this.gmarkers.some(o => o.officeId === MapData.locations[0].officeId);
          if (!exist) {
            this._markerInfo(MapData, 50);
          }
        });
      }
    }
  }

  generateDetailMarkers(locations, count) {
    if (locations.length > 0) {
      const len = count < locations.length ? count : locations.length;
      for (let i = 0; i < len; i++) {
        const location = locations[i];
        if (location) {
          this._markerInfo(location, i, true);
        }
      }
    }
  }

  generateSheetMarkers(MapData) {
    this.resetMarkers();
    if (MapData) {
      const location = MapData.locations[0];
      if (location) {
        location.isInView = false;
        this._markerInfo(MapData, 0);
        if (!this.currentLocationPushed) {
          this.pushCurrentLocation();
        }
        if (this.map) {
          this.gmarkers.forEach(m => {
            this.map.markers.add(m);
          });
        }
        this.fitBounds();
      }
    }
  }

  private _markerInfo(data, i, detail?) {
    let iconUrl = '';
    const location = detail ? data : data.locations[0];
    if (detail) { location.officeAddress = location.address; }
    if (location.isInView) {
      iconUrl = this.MAP_MARKER_HIGHLIGHT_ICON_URL;
    } else {
      if (this.showPromoted && location.isUpmcOwnedLocation) {
        iconUrl = this.MAP_MARKER_UPMC_ICON_URL;
      } else {
        iconUrl = this.MAP_MARKER_ICON_URL;
      }
    }

    const style = `background-image:url('${iconUrl}'); width: 34px; height: 36px; color: #ffffff; font-weight: bold; font-size: 12px; display: flex; align-items: center; justify-content: center; padding-bottom: 2px;`;
    // let gMarker = new atlas.HtmlMarker({
    const position = [
      location.officeAddress.coordinates.longitude,
      location.officeAddress.coordinates.latitude
    ];
    let gMarker = new atlas.HtmlMarker({
      position: position,
      htmlContent: '<div style="' + style + '">{text}</div>',
      text: detail ? location.address.letter : data.letter ? data.letter : 'A'
    });

    gMarker["markerInfo"] = {
      currentLocation: false,
      addressInfo: this.getAddress(location),
      officeName: location.officeName,
      letter: detail ? location.address.letter : data.letter,
      location: location
    };

    this.map.events.add('click', gMarker, (e: any) => {
      this.clickedMarker(gMarker);
    });

    this.gmarkers.push(gMarker);
    if (this.bounds) {
      this.bounds.push(position);
    }
  }

  pushCurrentLocation() {
    const style = `background-image:url('${this.MAP_MARKER_CURRENT_LOCATION}'); width: 22px; height: 31px; z-index: 100;`;
    const _lat = this.searchObjectService.getLat();
    const _lng = this.searchObjectService.getLong();
    const position = [
      _lng,
      _lat
    ];
    let gMarker = new atlas.HtmlMarker({
      position: position,
      htmlContent: '<div style="' + style + '"></div>'
    });

    gMarker["markerInfo"] = {
      currentLocation: true,
      addressInfo: '',
      officeName: '',
      location: []
    };

    this.map.events.add('click', gMarker, (e: any) => {
      this.clickedMarker(gMarker);
    });

    this.gmarkers.push(gMarker);
    if (this.bounds) {
      this.bounds.push(position);
    }
  }

  updateMarkers(m?: any) {
    if (this.gmarkers.length > 0) {
      let iconUrl = '';
      let zindex = 0;
      this.gmarkers.forEach((marker, i) => {
        if (!marker.markerInfo.currentLocation) {
          if (marker && marker.markerInfo.location) {
            if (marker.markerInfo.location.isInView) {
              iconUrl = this.MAP_MARKER_HIGHLIGHT_ICON_URL;
              zindex = (101 + i);
            } else {
              if (this.showPromoted && marker.markerInfo.location.isUpmcOwnedLocation) {
                iconUrl = this.MAP_MARKER_UPMC_ICON_URL;
              } else {
                iconUrl = this.MAP_MARKER_ICON_URL;
              }
              zindex = (50 - i);
            }
            const style = `background-image:url('${iconUrl}'); width: 34px; height: 36px; color: #ffffff; font-weight: bold; font-size: 12px; display: flex; align-items: center; justify-content: center; padding-bottom: 2px;`;
            this.renderer.setStyle(marker.element, 'z-index', zindex);
            this.renderer.setStyle(marker.element.firstChild, 'background-image', `url('${iconUrl}')`);
          }
        } else {
          this.renderer.setStyle(marker.element, 'z-index', 100);
        }
      });
    }

    if (m && !m.markerInfo.currentLocation) {
      const style = `background-image:url('${this.MAP_MARKER_HIGHLIGHT_ICON_URL}'); width: 34px; height: 36px; color: #ffffff; font-weight: bold; font-size: 12px; display: flex; align-items: center; justify-content: center; padding-bottom: 2px;`;
      m.setOptions({
        htmlContent: '<div style="' + style + '">{text}</div>'
      })
    }
  }

  removeViewfromMarkers() {
    this.gmarkers.forEach(marker => {
      marker.markerInfo.location.isInView = false;
    });
  }

  resetMarkers() {
    this.gmarkers.forEach(m => {
      this.map.markers.remove(m);
    });
    this.gmarkers = [];
  }

  public clickedMarker(m: any) {
    let content = '';
    if (m.markerInfo.currentLocation) {
      content = `<div style="padding:10px; box-shadow: 0 2px 7px 1px rgba(0,0,0,.3);"><strong>Your search location</strong></div>`;
    } else {
      content = `<div style="padding:10px; box-shadow: 0 2px 7px 1px rgba(0,0,0,.3);"><strong>${m.markerInfo.officeName}</strong><br><strong>${m.markerInfo.addressInfo}</strong></div>`;
    }
    if (this.markerPopup) {
      this.markerPopup.close();
    }
    this.markerPopup = new atlas.Popup();
    this.markerPopup.setOptions({
      pixelOffset: [0, -40],
      content: content,
      position: m.getOptions().position
    });
    this.markerPopup.open(this.map);
    this.renderer.setStyle(this.markerPopup.containerDiv, 'z-index', 200);
    this.removeViewfromMarkers();
    this.markerClickEvent.emit(m.markerInfo.letter);
    this.updateMarkers(m);
  }


  fitBounds() {
    if (this.map && this.bounds) {
      const bbox = atlas.data.BoundingBox.fromPositions(this.bounds);
      this.map.setCamera({
        bounds: bbox,
        padding: 50
      });
      this.map.resize();
    }
  }

  getAddress(location: any) {
    let officeAddress = location.officeAddress;
    let address = '';
    if (officeAddress) {
      if (officeAddress.addressLine1) {
        address += officeAddress.addressLine1;
      }
      if (officeAddress.addressLine2) {
        if (address !== '') {
          address += ', ';
        }
        address += officeAddress.addressLine2;
      }
      if (officeAddress.city) {
        if (address !== '') {
          address += ', ';
        }
        address += officeAddress.city;
      }
      if (officeAddress.state) {
        if (address !== '') {
          address += ', ';
        }
        address += officeAddress.state;
      }
      if (officeAddress.zipCode) {
        if (address !== '') {
          address += ' ';
        }
        address += officeAddress.zipCode;
      }

      if (location.phone) {
        if (address !== '') {
          address += ' <br />Phone : ';
        }
        address += location.phone.replace(/\D+/g, '')
          .replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
      }
    }
    return address;
  }
}
